import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {HubConnection, HubConnectionBuilder} from '@microsoft/signalr';
import notify from 'devextreme/ui/notify';
import {
    AuthService,
    FileImportsClient,
    FileImportStatus,
    FileImportStatusDto,
    GetFileImportStatusQuery,
    ImportFileType,
    ImportNotification,
} from '@services';
import {ImportFileTypePipe} from '../pipes/Import-file-type.pipe';

export interface UploadCard {
    id: string;
    fileType: ImportFileType;
    title: string;
    status: FileImportStatus;
    messageLines: string[];
    fileImportIds: string[];
    minimized: boolean;
    done: boolean;
    createdAt: number;
}

const STORAGE_PREFIX = 'hopewell.uploadCards.v1:';
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 180;
const CARD_MAX_AGE_MS = 24 * 60 * 60 * 1000;

@Injectable({providedIn: 'root'})
export class UploadManagerService {
    private http = inject(HttpClient);
    private authService = inject(AuthService);
    private fileImportsClient = inject(FileImportsClient);
    private importFileTypePipe = new ImportFileTypePipe();

    private readonly _cards = signal<UploadCard[]>([]);
    readonly cards = this._cards.asReadonly();
    readonly expandedCard = computed(() => this._cards().find(c => !c.minimized) ?? null);
    readonly minimizedCards = computed(() => this._cards().filter(c => c.minimized));
    readonly hasExpanded = computed(() => !!this.expandedCard());

    private hubConnection: HubConnection | undefined;
    private initialized = false;
    private readonly pollTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private readonly pollAttempts = new Map<string, number>();
    private readonly pendingUploads = new Set<string>();

    constructor() {
        this.authService.isAuthenticated$().subscribe(isAuth => {
            if (!isAuth) this.reset();
        });
    }

    init(): void {
        if (this.initialized) return;
        this.initialized = true;

        this.restore();
        void this.startSignalR();
        this.reconcileFromServer();

        for (const card of this._cards()) {
            if (!card.done && card.fileImportIds.length) this.startPolling(card.id);
        }
    }

    isTypeActive(fileType: ImportFileType): boolean {
        return this._cards().some(c => c.fileType === fileType && (c.status === FileImportStatus.Uploading || c.status === FileImportStatus.Processing));
    }

    startUpload(fileType: ImportFileType, files: File[], allowMultiple: boolean): void {
        if (!allowMultiple && files.length !== 1) {
            notify('Only one file is allowed for this import type', 'warning', 3000);
            return;
        }
        if (this.isTypeActive(fileType)) {
            notify(`An import of type '${this.importFileTypePipe.transform(fileType)}' is already in progress.`, 'warning', 4000);
            return;
        }

        const card = this.newCard(fileType, FileImportStatus.Uploading, false);
        this.pendingUploads.add(card.id);
        // New upload opens expanded; collapse any other open window so only one is expanded.
        this._cards.set([...this._cards().map(c => ({...c, minimized: true})), card]);
        this.persist();

        const formData = new FormData();
        files.forEach(file => formData.append('files', file, file.name));

        const url = `/api/FileImports/ImportFile?fileType=${encodeURIComponent('' + fileType)}`;
        this.http.post<string[]>(url, formData).subscribe({
            next: (ids: string[]) => {
                this.pendingUploads.delete(card.id);
                this.patchCard(card.id, c => {
                    c.fileImportIds = ids ?? [];
                    c.status = FileImportStatus.Processing;
                });
                this.startPolling(card.id);
            },
            error: (err: unknown) => {
                this.pendingUploads.delete(card.id);
                notify(this.extractError(err), 'error', 5000);
                this.removeCard(card.id);
            }
        });
    }

    minimize(cardId: string): void {
        this.patchCard(cardId, c => c.minimized = true);
    }

    maximize(cardId: string): void {
        // Only one card expanded at a time — collapse the others.
        this._cards.set(this._cards().map(c => ({...c, minimized: c.id !== cardId})));
        this.persist();
    }

    close(cardId: string): void {
        // 'Done' cards can always be dismissed; in-progress cards only from the minimized state.
        const card = this.getCard(cardId);
        if (card && (card.done || card.minimized)) this.removeCard(cardId);
    }

    private async startSignalR(): Promise<void> {
        if (this.hubConnection) return;

        const connection = new HubConnectionBuilder()
            .withUrl('/hubs/import', {accessTokenFactory: () => this.authService.getAccessToken() ?? ''})
            .withAutomaticReconnect()
            .build();

        connection.on('ImportNotification', (n: ImportNotification) => this.onNotification(n));
        connection.onreconnected(() => this.reconcileFromServer());

        this.hubConnection = connection;
        await connection.start().catch(err => console.error('Error starting SignalR connection:', err));
    }

    private async stopSignalR(): Promise<void> {
        const connection = this.hubConnection;
        if (!connection) return;
        this.hubConnection = undefined;
        connection.off('ImportNotification');
        try {
            await connection.stop();
        } catch (err) {
            console.error('Error stopping SignalR connection:', err);
        }
    }

    private onNotification(n: ImportNotification): void {
        if (n.status == null) return;
        const status = n.status;
        const card = this._cards().find(c => c.fileType === n.fileType && !c.done);
        if (!card) return;

        const terminal = status === FileImportStatus.Completed || status === FileImportStatus.Failed;
        this.patchCard(card.id, c => {
            if (n.message) {
                const last = c.messageLines[c.messageLines.length - 1];
                if (last !== n.message) c.messageLines = [...c.messageLines, n.message!];
            }
            c.status = status;
            if (terminal) c.done = true;
        });
        if (terminal) this.stopPolling(card.id);
    }

    private startPolling(cardId: string): void {
        this.stopPolling(cardId);
        this.pollAttempts.set(cardId, 0);
        this.schedulePoll(cardId);
    }

    private schedulePoll(cardId: string): void {
        this.pollTimers.set(cardId, setTimeout(() => this.poll(cardId), POLL_INTERVAL_MS));
    }

    private stopPolling(cardId: string): void {
        const handle = this.pollTimers.get(cardId);
        if (handle) clearTimeout(handle);
        this.pollTimers.delete(cardId);
        this.pollAttempts.delete(cardId);
    }

    private poll(cardId: string): void {
        const card = this.getCard(cardId);
        if (!card || card.done || !card.fileImportIds.length) {
            this.stopPolling(cardId);
            return;
        }

        const attempts = (this.pollAttempts.get(cardId) ?? 0) + 1;
        this.pollAttempts.set(cardId, attempts);
        if (attempts > POLL_MAX_ATTEMPTS) {
            this.stopPolling(cardId);
            this.patchCard(cardId, c => {
                c.status = FileImportStatus.Failed;
                c.done = true;
                c.messageLines = [...c.messageLines, 'The import is taking longer than expected. Please check the upload history.'];
            });
            return;
        }

        this.fileImportsClient.getFileImportStatus(new GetFileImportStatusQuery({ids: card.fileImportIds})).subscribe({
            next: statuses => {
                const allFinished = !!statuses && statuses.length > 0
                    && statuses.every(s => s.status !== FileImportStatus.Processing);
                if (!allFinished) {
                    this.schedulePoll(cardId);
                    return;
                }
                this.stopPolling(cardId);
                const current = this.getCard(cardId);
                if (current && !current.done) this.finalizeFromStatus(cardId, statuses);
            },
            error: () => this.schedulePoll(cardId)
        });
    }

    private finalizeFromStatus(cardId: string, statuses: FileImportStatusDto[]): void {
        const failed = statuses.filter(s => s.status === FileImportStatus.Failed);
        this.patchCard(cardId, c => {
            if (failed.length) {
                const messages = failed.map(s => s.message).filter(Boolean).join('\n');
                if (messages && !c.messageLines.includes(messages)) c.messageLines = [...c.messageLines, messages];
                c.status = FileImportStatus.Failed;
            } else {
                const messages = statuses.map(s => s.message).filter(Boolean).join('\n');
                if (messages && c.messageLines.length === 0) c.messageLines = [messages];
                c.status = FileImportStatus.Completed;
            }
            c.done = true;
        });
    }

    private reconcileFromServer(): void {
        // Only reconcile imports this client started — their ids are stored on the restored cards.
        const ids = Array.from(new Set(
            this._cards().filter(c => !c.done).flatMap(c => c.fileImportIds)
        ));
        if (!ids.length) return;

        this.fileImportsClient.getFileImportStatus(new GetFileImportStatusQuery({ids})).subscribe({
            next: statuses => this.applyServerRows(statuses ?? []),
            error: () => { /* leave cards as-is; polling still runs */
            }
        });
    }

    private applyServerRows(statuses: FileImportStatusDto[]): void {
        const byId = new Map<string, FileImportStatusDto>();
        for (const s of statuses) {
            if (s.id != null) byId.set(s.id, s);
        }

        for (const card of this._cards()) {
            if (card.done || !card.fileImportIds.length) continue;

            const known = card.fileImportIds
                .map(id => byId.get(id))
                .filter((s): s is FileImportStatusDto => !!s);
            if (!known.length) continue; // server hasn't recorded these ids yet

            if (known.every(s => s.status !== FileImportStatus.Processing)) {
                this.stopPolling(card.id);
                this.finalizeFromStatus(card.id, known);
            } else {
                if (card.status === FileImportStatus.Uploading) {
                    this.patchCard(card.id, c => c.status = FileImportStatus.Processing);
                }
                this.startPolling(card.id);
            }
        }
    }

    private storageKey(): string {
        return `${STORAGE_PREFIX}${this.authService.userId ?? 'anon'}`;
    }

    private persist(): void {
        try {
            localStorage.setItem(this.storageKey(), JSON.stringify(this._cards()));
        } catch { /* ignore quota/serialization errors */
        }
    }

    private restore(): void {
        try {
            const raw = localStorage.getItem(this.storageKey());
            if (!raw) return;
            const cards = JSON.parse(raw) as UploadCard[];
            const cutoff = Date.now() - CARD_MAX_AGE_MS;
            this._cards.set((cards ?? []).filter(c => c && c.createdAt >= cutoff));
        } catch { /* corrupt storage — ignore */
        }
    }

    private clearStorage(): void {
        try {
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i);
                if (key && key.startsWith(STORAGE_PREFIX)) localStorage.removeItem(key);
            }
        } catch { /* ignore */
        }
    }

    private newCard(fileType: ImportFileType, status: FileImportStatus, minimized: boolean): UploadCard {
        return {
            id: crypto.randomUUID(),
            fileType,
            title: this.importFileTypePipe.transform(fileType),
            status,
            messageLines: [],
            fileImportIds: [],
            minimized,
            done: status === FileImportStatus.Completed || status === FileImportStatus.Failed,
            createdAt: Date.now()
        };
    }

    private getCard(id: string): UploadCard | undefined {
        return this._cards().find(c => c.id === id);
    }

    private patchCard(id: string, mutate: (card: UploadCard) => void): void {
        const cards = this._cards();
        const index = cards.findIndex(c => c.id === id);
        if (index < 0) return;
        const clone = {...cards[index]};
        mutate(clone);
        const next = [...cards];
        next[index] = clone;
        this._cards.set(next);
        this.persist();
    }

    private removeCard(id: string): void {
        this.stopPolling(id);
        this._cards.set(this._cards().filter(c => c.id !== id));
        this.persist();
    }

    private extractError(err: unknown): string {
        const body = (err as { error?: unknown })?.error;
        if (Array.isArray(body) && body.length && (body[0] as { message?: string })?.message) {
            return body.map((e: { message?: string }) => e.message).filter(Boolean).join('\n');
        }
        if (typeof body === 'string' && body) return body;
        return (err as { message?: string })?.message ?? 'Upload failed.';
    }

    private reset(): void {
        for (const id of Array.from(this.pollTimers.keys())) this.stopPolling(id);
        this.pendingUploads.clear();
        void this.stopSignalR();
        this._cards.set([]);
        this.clearStorage();
        this.initialized = false;
    }
}
